"use client";

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Check, AlertCircle } from "lucide-react";

interface ServerMessage {
    key: string;
    content: string;
    enabled: boolean;
}

const AdminServerMessage: React.FC = () => {
    const [serverMessage, setServerMessage] = useState<ServerMessage | null>(null);
    const [content, setContent] = useState<string>("");
    const [enabled, setEnabled] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    useEffect(() => {
        loadServerMessage();
    }, []);

    const loadServerMessage = async () => {
        setLoading(true);
        try {
            const response = await fetch("/api/admin/admin_texts/server_message");
            if (response.ok) {
                const data = await response.json();
                setServerMessage(data);
                setContent(data.content || "");
                setEnabled(data.enabled || false);
            } else {
                // Ha nem létezik, készítsd elő az üres állapotot
                setServerMessage(null);
                setContent("");
                setEnabled(false);
            }
        } catch (error) {
            console.error("Hiba az üzenet betöltésénél:", error);
            setMessage({ type: "error", text: "Hiba az üzenet betöltésénél" });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await fetch("/api/admin/admin_texts/server_message", {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
                },
                body: JSON.stringify({ content, enabled }),
                credentials: "include",
            });

            if (!response.ok) throw new Error("Mentési hiba");
            const data = await response.json();
            setServerMessage(data);
            setMessage({ type: "success", text: "Karbantartás beállítások sikeresen mentve!" });

            // Üzenet törlése 3 másodperc után
            setTimeout(() => setMessage(null), 3000);
        } catch (error) {
            console.error("Hiba:", error);
            setMessage({ type: "error", text: "Hiba a mentés során" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Card className="border border-border">
                <CardContent className="pt-6 flex justify-center">
                    <Loader2 className="animate-spin" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="border border-border">
            <CardHeader>
                <div className="flex items-start justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <AlertCircle className="text-amber-500" size={24} />
                            Szerver Karbantartás
                        </CardTitle>
                        <CardDescription>
                            Ha engedélyezed, az oldal csak a karbantartás üzenetet jeleníti meg
                        </CardDescription>
                    </div>
                    {enabled && (
                        <div className="px-3 py-1 bg-amber-500/20 border border-amber-500 rounded text-amber-400 text-sm font-semibold">
                            AKTÍV
                        </div>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Üzenet megjelenítés */}
                {message && (
                    <div
                        className={`p-4 rounded-md flex items-center gap-2 ${message.type === "success"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                            }`}
                    >
                        {message.type === "success" && <Check size={20} />}
                        {message.text}
                    </div>
                )}

                {/* Figyelmeztetés */}
                {enabled && (
                    <div className="p-4 bg-amber-900/30 border-2 border-amber-600 rounded-lg">
                        <p className="text-amber-200 font-semibold mb-2">⚠️ Figyelem!</p>
                        <p className="text-amber-200 text-sm">
                            A karbantartás mód jelenleg ENGEDÉLYEZVE van. Az oldal csak ezt az üzenetet jeleníti meg.
                        </p>
                    </div>
                )}

                {/* Szöveg szerkesztés */}
                <div className="space-y-2">
                    <label className="text-sm font-semibold">Karbantartás üzenet</label>
                    <Textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Írd ide a karbantartás üzenetet..."
                        rows={10}
                        className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                        A HTML és markdown ne támogatott. Új sorok (`\n`) használhatók.
                    </p>
                </div>

                {/* Engedélyezve checkbox */}
                <div className="flex items-center gap-4 p-4 bg-card border border-border rounded-lg">
                    <input
                        type="checkbox"
                        id="maintenance-enabled"
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        className="w-5 h-5 rounded cursor-pointer"
                    />
                    <label htmlFor="maintenance-enabled" className="flex-1 cursor-pointer">
                        <p className="font-semibold text-base">Karbantartás mód engedélyezése</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            Ha bejelölöd, az oldal helyett csak a fenti üzenet jelenik meg
                        </p>
                    </label>
                </div>

                {/* Mentés gomb */}
                <div className="flex gap-2 justify-end pt-4">
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-primary"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="animate-spin mr-2" size={16} />
                                Mentés...
                            </>
                        ) : (
                            <>
                                <Check className="mr-2" size={16} />
                                Mentés
                            </>
                        )}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default AdminServerMessage;
